// InlineSparkleExtension.js

import { Extension } from '@tiptap/core'
import { Plugin } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'
import React, { useState, useRef, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { Box, Button, IconButton, useTheme, useMediaQuery } from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import EditIcon from '@mui/icons-material/Edit'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { t } from "@lingui/macro";

export const InlineSparkleExtension = Extension.create({
  name: 'inlineSparkle',

  addProseMirrorPlugins() {
    let decorationsCache = null
    let prevDocSize = null
    const sparkleNodes = []

    return [
      new Plugin({
        props: {
          // 1) Render a widget at the end of each non-empty paragraph
          decorations(state) {
            const { doc } = state
            if (decorationsCache && prevDocSize === doc.content.size) {
              return decorationsCache
            }

            sparkleNodes.forEach(container => {
              try {
                container._unmount?.()
              } catch (e) {
                console.warn('Failed to unmount sparkle:', e)
              }
            })
            sparkleNodes.length = 0

            const decorations = []

            doc.descendants((node, pos) => {
              if (node.type.name === 'paragraph' && node.content.size > 0) {
                const container = document.createElement('span')
                container.className = 'sparkle-button-inline'
                container.contentEditable = 'false'

                container.addEventListener('touchstart', (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }, { passive: false });



                // ✅ Create and track React root
                const root = createRoot(container)
                root.render(<SparkleWithActions pos={pos} container={container} />)

                // ✅ Attach cleanup method
                container._unmount = () => {
                  try {
                    root.unmount()
                  } catch (e) {
                    console.warn('Sparkle already unmounted:', e)
                  }
                }

                sparkleNodes.push(container)

                decorations.push(
                  Decoration.widget(pos + node.nodeSize - 1, () => container, {
                    side: 1,
                    //ignore: true,
                    ignoreSelection: true,
                    destroy: (element) => {
                      element._unmount?.()
                    }
                  })
                )

              }
            })

            prevDocSize = doc.content.size
            decorationsCache = DecorationSet.create(doc, decorations)
            return decorationsCache
          },

          // 2) Let any event inside .sparkle-button-inline go to React
          stopEvent: event =>
            event.target?.closest('.sparkle-button-inline') !== null,
        },
      }),
    ]
  },
})

export default function SparkleWithActions({ pos, container }) {
  const [showActions, setShowActions] = useState(false)
  const [anchor, setAnchor] = useState({ x: 0, y: 0 })
  const wrapperRef = useRef(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Measure sparkle center on open
  const measureAnchor = () => {
    const rect = wrapperRef.current.getBoundingClientRect()
    setAnchor({
      x: rect.left + rect.width / 2,
      y: rect.top,
    })
  }

  // Re-measure on scroll so it follows vertically
  useEffect(() => {
    if (!showActions || !isMobile) return
    window.addEventListener('scroll', measureAnchor, true)
    return () => window.removeEventListener('scroll', measureAnchor, true)
  }, [showActions, isMobile])

  const toggleActions = (e) => {
    e.preventDefault()
    e.stopPropagation()
    measureAnchor()
    window.dispatchEvent(new Event('sparkle-close-all'))
    setTimeout(() => setShowActions(v => !v), 0)
  }

  const handleSelect = (type, e) => {
    e.preventDefault()
    e.stopPropagation()
    container.dispatchEvent(new CustomEvent('sparkle-action', {
      detail: { paragraphPos: pos, type },
      bubbles: true,
    }))
    setShowActions(false)
    if (document.activeElement && document.activeElement.contentEditable === 'true') {
      document.activeElement.blur()
    }

  }

  useEffect(() => {
    const closeAll = () => setShowActions(false)
    window.addEventListener('sparkle-close-all', closeAll)
    return () => window.removeEventListener('sparkle-close-all', closeAll)
  }, [])

  useEffect(() => {
    const onClickOutside = e => {
      if (!wrapperRef.current?.contains(e.target)) setShowActions(false)
    }
    window.addEventListener('click', onClickOutside)
    return () => window.removeEventListener('click', onClickOutside)
  }, [])

  const isLeftSide = () => {
    const rect = wrapperRef.current?.getBoundingClientRect()
    return rect ? rect.left < window.innerWidth / 2 : false
  }

  const shouldFlipDown = () => {
    const rect = wrapperRef.current?.getBoundingClientRect()
    return rect ? rect.top < 80 : false
  }


  return (
    <Box
      ref={wrapperRef}
      onMouseDown={e => { e.preventDefault(); e.stopPropagation() }}
      sx={{ position: 'relative', display: 'inline-block' }}
    >
      {showActions && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: isLeftSide() ? '100%' : 'auto',
            right: isLeftSide() ? 'auto' : '100%',
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 1,
            p: 1,
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            border: `4px solid #ffc858`,
            borderRadius: 4,
            boxShadow: 3,
            zIndex: 0,
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',

          }}
        >

          <Button
            size="small"
            variant="contained"
            sx={{
              fontSize: '0.65rem',
              padding: '2px 6px',
              minWidth: 'auto',
              lineHeight: 1.2,
              color: 'black',
              bgcolor: '#ffc858',
              borderRadius: 1,
            }}
            startIcon={<EditIcon />}
            onTouchStart={(e) => handleSelect('edit', e)}
            onMouseDown={(e) => handleSelect('edit', e)}
          >
            {t`Edit Paragraph`}
          </Button>
          <Button
            size="small"
            variant="conained"
            sx={{
              fontSize: '0.65rem',
              padding: '2px 6px',
              minWidth: 'auto',
              lineHeight: 1.2,
              color: 'white',
              bgcolor: '#ec494c',
              borderRadius: 1,
            }}
            startIcon={<AutoFixHighIcon />}
            onTouchStart={(e) => handleSelect('continue', e)}
            onMouseDown={(e) => handleSelect('continue', e)}
          >
            {t`Continue Writing`}
          </Button>
        </Box>
      )
      }

      <IconButton
        size="small"
        sx={{
          p: 0.5,
          ml: 0.5,
          color: 'gold',
          bgcolor: 'transparent',
          width: 24,
          height: 24,
          transition: 'all 0.2s ease-in-out',
          '&:hover': { transform: 'scale(1.2)' },
        }}
        onTouchStart={toggleActions}
        onMouseDown={e => { e.preventDefault(); e.stopPropagation() }}
        onClick={toggleActions}
      >
        <AutoAwesomeIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box >
  )
}